import { useState, useEffect, useMemo } from 'react';
import { cleanPlayerData } from '@components/StatsConsole/Widgets/PlayersWidget/functions';
import { Row } from '@components/StatsConsole/Widgets/PlayersWidget/parts/Row';
import { ShowAllButton } from '@components/StatsConsole/Widgets/PlayersWidget/parts/ShowAllButton';
import { useLanguage } from '@utils/hooks/useLanguage';
import { useTranslations } from '../../../../../translation/TranslationContext';
import { useColorContext } from '@stores/ColorContext';

interface IPlayersComparisonProps {
  data: {
    label: string;
    rows: {
      playerHome: string | null;
      playerAway: string | null;
      home: number | null;
      away: number | null;
    }[];
  }
  noData?: boolean;
  playersTabActiveFilter: string | undefined;
}

const PlayersComparison = ({ data, noData = false, playersTabActiveFilter }: IPlayersComparisonProps) => {
  const language = useLanguage();
  const translations = useTranslations(language);

  const translatedLabel = useMemo(() => {
    const t = translations[data?.label as keyof typeof translations] as unknown as string;
    return typeof t === 'string' ? t : '';
  }, [translations, data?.label]);

  const [isShowAllOpen, setIsShowAllOpen] = useState(false);
  const [visibleRows, setVisibleRows] = useState<IPlayersComparisonProps['data']['rows']>([]);
  const [colNumberWidthHome, setColNumberWidthHome] = useState(0);
  const [colNumberWidthAway, setColNumberWidthAway] = useState(0);
  const { colors } = useColorContext()

  function findMaxValuesOfPlayersComparison(statistics: any) {
    if (!statistics) {
      return [0, 0];
    }

    const rows = statistics;
    let maxHome = 0;
    let maxAway = 0;

    for (const row of rows) {
      if (row.home > maxHome) {
        maxHome = row.home;
      }
      if (row.away > maxAway) {
        maxAway = row.away;
      }
    }

    return [maxHome, maxAway];
  }

  const calculateWidth = (text: string): number => {
    const length = text.length;
    if (length === 0) return 0;
    if (length === 1) return 15;
    if (length === 2) return 22;
    if (length >= 3) return 29 + (length - 3) * 7;
    return 0;
  };


  useEffect(() => {
    if (noData) {
      setVisibleRows(data?.rows)
    } else if (data?.rows.length === 1) {
      setVisibleRows(data?.rows)
    } else {
      const cleanedRows = cleanPlayerData(data?.rows);
      setVisibleRows(isShowAllOpen ? cleanedRows : cleanedRows.slice(0, 10));
    }
  }, [data?.rows, isShowAllOpen, noData]);


  useEffect(() => {
    const maxValues = findMaxValuesOfPlayersComparison(data?.rows)
    setColNumberWidthHome(calculateWidth(maxValues[0].toString()))
    setColNumberWidthAway(calculateWidth(maxValues[1].toString()))

  }, [data]);

  useEffect(() => {
    setIsShowAllOpen(false)
  }, [playersTabActiveFilter])

  return (
    <div data-testid="players-comparison" className="flex flex-col justify-start items-center w-full ">
      <div data-testid={`players-comparison-header-${data?.label.toLowerCase()}`}
        className={`w-full flex justify-center items-center text-[12px] leading-[16px] 
            text-[${colors?.textColor}] font-robotoMedium font-medium mb-[8px] select-none`}
      >
        {translatedLabel}
      </div>
      <div data-testid="players-comparison-rows" className="flex flex-col justify-start items-center w-full">
        {visibleRows.map((row, index) => (
          <Row
            key={index}
            index={index}
            playerHome={row.playerHome}
            playerAway={row.playerAway}
            home={row.home} away={row.away} 
            colNumberWidthHome={colNumberWidthHome} 
            colNumberWidthAway={colNumberWidthAway} />
        ))}
      </div>
      {data?.rows.length > 10 ? (
        <ShowAllButton
          isShowAllOpen={isShowAllOpen}
          setIsShowAllOpen={setIsShowAllOpen}
        />
      ) : (
        <div className="h-[16px]"></div>
      )
      }
    </div>
  )
}

export default PlayersComparison;
